/**
 * Streaming API services for FroggyAI and FroggerAI
 * Implements fetch-based SSE (the only transport that can carry an
 * `Authorization` header).
 *
 * ⚠️ THERE IS NO JSON-POST FALLBACK, AND THE DOCK NEVER RETRIES. A failed
 * stream used to be re-issued twice and then re-asked as a JSON POST — up
 * to FOUR sends of one spoken sentence, each of which the server runs to
 * the end (it keeps going after the client disconnects). See
 * `RETRY_NEVER` / `RETRY_BEFORE_RESPONSE` below and
 * `services/__tests__/neverReSendATurn.test.js`.
 */

import { newTurnId } from '../utils/turnId';

// Get API base URL from existing configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// FroggerAI v2 switch - HARDCODED TO TRUE for LangGraph backend
const FROGGERAI_V2_RAW = process.env.REACT_APP_FROGGERAI_V2 || '1';  // Default to v2
const USE_FROGGERAI_V2 = true;  // FORCED: Always use v2 (LangGraph) endpoint

// Debug: Log v2 config at module load (visible in browser console)
console.log('[FroggerAI Config] 🚀', {
  USE_FROGGERAI_V2,
  FROGGERAI_V2_RAW,
  API_BASE_URL,
  streamPath: USE_FROGGERAI_V2 ? '/ask/stream/v2' : '/ask/stream',
  note: USE_FROGGERAI_V2 ? 'USING V2 (LangGraph)' : 'USING V1 (Legacy)'
});

/**
 * WHEN A STREAM MAY BE RE-ISSUED — the rule is "a turn the server may have
 * received is never sent again automatically".
 *
 * `RETRY_NEVER` — the staff FroggerAI dock (and the default for any new
 * caller). Every FroggerAI sentence can write: recordFeeding, logEvent,
 * runRoutine, undo, a confirm "yes", doneTask. Once the request is handed
 * to fetch nothing on this side can know whether it landed, so a failure
 * ENDS the turn and the keeper is told the request may still have gone
 * through.
 *
 * `RETRY_BEFORE_RESPONSE` — the customer FroggyAI surfaces. Every FroggyAI
 * tool is a READ (catalog, species, care guide, policy, FAQ, about,
 * knowledge, web search); "Talk to a human" is its own POST from its own
 * modal, never a streamed turn. A retry is still allowed while NO SSE
 * stream has opened (a refused fetch, a 5xx, a 401/403, a non-SSE body),
 * because then nothing has been painted and nothing writes. Once a stream
 * is open, no retry: that is what painted the answer twice.
 */
export const RETRY_NEVER = 'never';
export const RETRY_BEFORE_RESPONSE = 'before_response';

/**
 * ── A STOPPED TURN REMEMBERS ONLY WHAT THE SCREEN SHOWED (Fable brief
 * 2026-10-23, item E) ───────────────────────────────────────────────────
 *
 * The keeper pressed Stop mid-answer; the server's worker still ran the
 * turn to the end (it must — a landed write keeps its Undo) and memory
 * stored the WHOLE reply, so the next turn "corrected" words that were
 * never on screen. Only the dock knows what the screen showed, so its NEXT
 * request carries `stopped_turn: {turn_id, shown_text, nothing_shown}`
 * and the server marks that turn before it appends the new sentence.
 *
 * ⚠️ The streamed turn is a GET and gunicorn refuses a request line past
 * ~4 KB (`limit_request_line`), so the JSON is trimmed to fit
 * `STOPPED_TURN_URL_BUDGET` with the rest of the URL. A trimmed text is
 * the FRONT of what was shown and ends in "…" — memory may hold LESS than
 * the screen, never more.
 */
export const STOPPED_TURN_URL_BUDGET = 3500;
export const STOPPED_TURN_MAX_CHARS = 2000;

export function stoppedTurnParam(stoppedTurn, usedChars = 0) {
  if (!stoppedTurn || !stoppedTurn.turn_id) return '';
  const whole = String(stoppedTurn.shown_text || '').trim();
  const nothingShown = !whole || !!stoppedTurn.nothing_shown;
  const encode = (text) => `&stopped_turn=${encodeURIComponent(JSON.stringify({
    turn_id: String(stoppedTurn.turn_id),
    shown_text: nothingShown ? '' : text,
    nothing_shown: nothingShown,
  }))}`;
  let keep = Math.min(whole.length, STOPPED_TURN_MAX_CHARS);
  const cut = (n) => (n >= whole.length ? whole : `${whole.slice(0, n).trimEnd()}…`);
  let param = encode(cut(keep));
  while (!nothingShown && keep > 0 && usedChars + param.length > STOPPED_TURN_URL_BUDGET) {
    keep = Math.floor(keep * 0.8);
    param = encode(cut(keep));
  }
  // Even an empty cut ("…") is still "something was shown" — never a
  // false "nothing shown". A URL with no room at all sends nothing.
  if (usedChars + param.length > STOPPED_TURN_URL_BUDGET) return '';
  return param;
}

/**
 * The server's own sentence out of a refusal body, or null. Only a human
 * sentence is taken — `detail`, `message` or `error` as a string with a
 * space in it — so a machine code (`method_not_allowed`) or an HTML error
 * page is never printed to a keeper, and neither is anything carrying an
 * address or a stack trace (`LEAKY_SENTENCE`).
 */
// Never shown to a keeper: an address (`@` — the 2026-09-11 rule, no
// operator surface hands out an email) or a stack trace (`Traceback`, a
// `File "…"` frame line).
const LEAKY_SENTENCE = /@|Traceback|File "/;

export function serverSentenceFrom(body) {
  if (!body || typeof body !== 'string') return null;
  let data;
  try { data = JSON.parse(body); } catch (e) { return null; }
  if (!data || typeof data !== 'object') return null;
  for (const key of ['detail', 'message', 'error']) {
    const v = data[key];
    if (typeof v === 'string') {
      const t = v.trim();
      if (t && /\s/.test(t) && t.length <= 300 && !LEAKY_SENTENCE.test(t)) return t;
    }
  }
  return null;
}

/**
 * Parse Server-Sent Events from a readable stream
 * @param {Response} response - Fetch response with SSE body
 */
export async function* readSSE(response) {
  const reader = response.body.getReader();
  const dec = new TextDecoder('utf-8');
  let buf = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;

      buf += dec.decode(value, { stream: true });
      let idx;

      while ((idx = buf.indexOf('\n\n')) !== -1) {
        const raw = buf.slice(0, idx);
        buf = buf.slice(idx + 2);

        const line = raw.split('\n').find(l => l.startsWith('data:'));
        if (!line) continue;

        const data = line.slice(5).trim();
        if (data === '') continue; // Skip empty data lines

        yield data;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export class StreamingAPIService {
  constructor() {
    this.activeStreams = new Map();
    this.abortControllers = new Map();
    this.inactivityTimers = new Map();
    this.lastActivity = new Map();
    this.inactivityTimeout = 30 * 1000; // 30 seconds
  }

  /**
   * Stream ask with readSSE generator
   * @param {string} facilityId - Facility UUID
   * @param {string} question - User question
   * @param {AbortSignal} signal - Abort signal for cancellation
   * @param {number} timeoutMs - Timeout in milliseconds (default 7000)
   * @returns {AsyncGenerator} SSE data chunks
   */
  async* streamAsk(facilityId, question, signal = null, timeoutMs = 7000) {
    // Use facility-scoped URL if facilityId is valid, otherwise use global endpoint
    const hasFacility = facilityId && facilityId !== 'null' && facilityId !== 'undefined';
    const url = hasFacility
      ? `${API_BASE_URL}/facility/${facilityId}/advisor/froggy/ask/stream?q=${encodeURIComponent(question)}`
      : `${API_BASE_URL}/facility/advisor/froggy/ask/stream?q=${encodeURIComponent(question)}`;

    if (!hasFacility) {
      console.warn('[FroggyAI] streamAsk called without facilityId, using global endpoint');
    }

    const controller = new AbortController();
    if (signal) {
      signal.addEventListener('abort', () => controller.abort());
    }

    // Set timeout
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream'
        },
        credentials: 'include',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      // Use readSSE generator to parse the response
      for await (const data of readSSE(response)) {
        if (controller.signal.aborted) break;
        yield data;
      }

    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        const timeoutError = new Error('Stream timeout after 7 seconds');
        timeoutError.fallback_reason = 'timeout';
        throw timeoutError;
      }

      error.fallback_reason = error.status >= 500 ? 'server_error' : 'client_error';
      throw error;
    }
  }

  /**
   * FroggyAI streaming endpoint (public, no auth)
   * @param {string} facilityId - Facility UUID
   * @param {string} question - User question
   * @param {function} onMessage - Callback for stream messages
   * @param {function} onError - Error callback
   * @param {function} onComplete - Completion callback
   * @param {function} onOpen - Optional; handed the `{close}` handle
   *   SYNCHRONOUSLY, before the first byte (the returned promise only
   *   settles once the stream is OVER — see `_createFetchSSE`). The
   *   full-page FroggyAI chat's Stop closes the stream through it.
   */
  streamFroggyAI(facilityId, question, onMessage, onError, onComplete, strictMode = null, conversationId = null, onOpen = null) {
    // Use facility-scoped URL if facilityId is valid, otherwise use global endpoint
    const hasFacility = facilityId && facilityId !== 'null' && facilityId !== 'undefined';
    // ⚠️ "NEW CHAT" IS A NEW CONVERSATION ON THE SERVER (Cowork run 38,
    // H1). The server keys a FroggyAI conversation off the session cookie,
    // so without this the bubble's New chat cleared the screen and the
    // server went on replaying the old conversation. The bubble mints an
    // id per conversation; the server mixes it into its own key.
    const conversation = conversationId
      ? `&conversation=${encodeURIComponent(conversationId)}`
      : '';
    const streamUrl = hasFacility
      ? `${API_BASE_URL}/facility/${facilityId}/advisor/froggy/ask/stream?q=${encodeURIComponent(question)}${conversation}`
      : `${API_BASE_URL}/facility/advisor/froggy/ask/stream?q=${encodeURIComponent(question)}${conversation}`;

    if (!hasFacility) {
      console.warn('[FroggyAI] streamFroggyAI called without facilityId, using global endpoint');
    }

    // Use passed parameter or default from env
    const actualStrictMode = strictMode !== null ? strictMode : (process.env.REACT_APP_FF_STREAM_STRICT !== '0');

    // Include facility ID header for context (only if valid)
    const headers = {
      'Accept': 'text/event-stream'
    };
    if (hasFacility) {
      headers['X-Facility-Id'] = facilityId;
    }

    console.group('[FroggyAI] SSE Frontend Call Details');
    console.log('Final URL string:', streamUrl);
    console.log('Method:', 'GET');
    console.log('Headers:', headers);
    console.log('Authorization header included:', 'NO (FroggyAI uses cookie auth)');
    console.log('X-Facility-Id header included:', facilityId ? 'YES' : 'NO (no facilityId)');
    console.log('Request body:', 'NONE (GET request)');
    console.log('Content-Type header:', 'NOT SET (correct for GET)');
    console.log('Strict mode:', actualStrictMode ? 1 : 0);
    console.groupEnd();

    return this._createFetchSSE(streamUrl, {
      headers,
      strictMode: actualStrictMode,
      // Customer FroggyAI is read-only: a retry is allowed only while no
      // stream has opened. See `RETRY_BEFORE_RESPONSE`.
      retry: RETRY_BEFORE_RESPONSE,
      onMessage,
      onError,
      onComplete,
      onOpen
    });
  }

  /**
   * FroggerAI streaming endpoint (requires JWT auth)
   * @param {string} facilityId - Facility UUID
   * @param {string} question - User question
   * @param {string} authToken - JWT Bearer token
   * @param {function} onMessage - Callback for stream messages
   * @param {function} onError - Error callback
   * @param {function} onComplete - Completion callback
   * @param {function} onOpen - Handed the `{close}` handle SYNCHRONOUSLY,
   *   before the first byte. See `_createFetchSSE`: the returned promise
   *   only settles once the stream is OVER, so a caller that needs to
   *   abort a stalled turn cannot wait for it (run 13, F1).
   */
  streamFroggerAI(facilityId, question, authToken, onMessage, onError, onComplete, conversationId = null, place = null, onOpen = null, turnId = null, stoppedTurn = null) {
    // Use v2 endpoint (LangGraph) - HARDCODED
    const streamPath = USE_FROGGERAI_V2 ? '/ask/stream/v2' : '/ask/stream';
    // Include conversation ID (cid) for session state persistence across requests
    const cidParam = conversationId ? `&cid=${encodeURIComponent(conversationId)}` : '';
    // I-03 R6 / S3-06.3 — WHERE THE KEEPER IS. `place` is `{rack_id,
    // enclosure_id, room_id}`, ids only; the backend
    // (`langgraph/views._parse_place`) accepts it on the query string as
    // JSON precisely because this endpoint streams over a GET, and it
    // re-reads every NAME from its own rows rather than trusting ours.
    //
    // OMITTED WHEN THERE IS NO PLACE, and that is not a detail: the
    // backend distinguishes "the caller said nothing about place" (keep
    // the sticky one) from "nowhere" (an empty object — forget it). A
    // dock on a page that is not a place must not silently wipe the rack
    // the keeper set two taps ago.
    // Item 10: an EXPLICIT `{}` is "nowhere" (the keeper tapped ×) and
    // goes on the wire; `null` — no place — still sends no field.
    const placeParam = (place && typeof place === 'object')
      ? `&place=${encodeURIComponent(JSON.stringify(place))}`
      : '';
    // ⚠️ ONE TURN, ONE RUN. `turn_id` is minted ONCE per user turn (the
    // caller passes it; a caller that does not gets one here, which is
    // still one per call, and nothing re-sends a call). The server claims
    // it before running anything (`langgraph/turn_claim.py`), so a browser
    // or proxy silently retrying this GET runs nothing the second time.
    const turnParam = `&turn_id=${encodeURIComponent(turnId || newTurnId())}`;
    const baseUrl = `${API_BASE_URL}/ai/${facilityId}${streamPath}?q=${encodeURIComponent(question)}${cidParam}${turnParam}${placeParam}`;
    // A STOPPED TURN REMEMBERS ONLY WHAT THE SCREEN SHOWED (Fable brief
    // 2026-10-23, item E). See `stoppedTurnParam`: the previous turn the
    // keeper pressed Stop on, and the text its bubble showed, ride on
    // THIS request so the server stores only those words.
    const streamUrl = `${baseUrl}${stoppedTurnParam(stoppedTurn, baseUrl.length)}`;

    // CRITICAL: Log exactly which endpoint is being called
    console.log('[FroggerAI] streamFroggerAI called', {
      USE_FROGGERAI_V2,
      streamPath,
      streamUrl,
      facilityId,
      conversationId,
      questionLength: question.length
    });

    return this._createFetchSSE(streamUrl, {
      headers: {
        'Accept': 'text/event-stream',
        'Authorization': `Bearer ${authToken}`
      },
      // Every FroggerAI sentence can WRITE. Never re-sent. See
      // `RETRY_NEVER`.
      retry: RETRY_NEVER,
      onMessage,
      onError,
      onComplete,
      onOpen
    });
  }

  // ⚠️ THERE IS NO EventSource PATH. `_createEventSource` lived here
  // and NOTHING has ever called it — every stream in this file goes
  // through `_createFetchSSE`, which is the only one that can send an
  // `Authorization` header (EventSource cannot, which is why the fetch
  // reader exists at all). It was a second, drifting copy of the chunk
  // dispatch: its `counts` arm was written in S3-06.4 for code no
  // keeper could reach, and a reader who found it first would have
  // believed the app reconnects three times and authenticates by
  // cookie. `services/__tests__/froggerStreamWire.test.js` keeps it
  // gone.

  /**
   * Create fetch-based SSE for authenticated requests
   */
  async _createFetchSSE(url, options = {}) {
    const {
      headers = {}, strictMode = false, retry = RETRY_NEVER,
      onMessage, onError, onComplete, onOpen,
    } = options;

    console.log(`[FroggyAI] _createFetchSSE start - url=${url}, headers=${JSON.stringify(headers)}, strict=${strictMode}, retry=${retry}`);
    let reconnectAttempts = 0;
    const maxReconnects = 2;
    let isCompleted = false;
    // Set the moment an SSE response is open and its body is about to be
    // read. From then on the server is RUNNING this turn and anything it
    // sent is already on screen, so a retry would run the turn twice and
    // paint its text twice. See `RETRY_BEFORE_RESPONSE`.
    let streamOpened = false;
    let abortController = new AbortController();

    this.abortControllers.set(url, abortController);

    // ⚠️ THE HANDLE IS HANDED OVER BEFORE THE FIRST BYTE (run 13, F1).
    //
    // This function ends in `await connect()`, so the promise it returns
    // does not settle until the stream is OVER — which means the
    // caller's `activeStreamRef = await askFroggerStream(…)` is null for
    // the entire life of the turn, and a stalled turn had nothing to
    // abort with. The SAME object is handed to `onOpen` now and returned
    // at the end, so "close it" means one thing however it was obtained.
    const handle = {
      close: () => {
        abortController.abort();
        this._closeStream(url);
      }
    };
    try { onOpen?.(handle); } catch (e) { console.warn('[SSE] onOpen threw:', e?.message); }

    const connect = async () => {
      try {
        const fetchOptions = {
          method: 'GET',
          headers: {
            'Accept': 'text/event-stream',
            ...headers
          },
          cache: 'no-store', // Prevent caching of SSE streams
          credentials: 'include', // Include cookies for auth
          signal: abortController.signal
        };

        console.log('[FroggyAI] fetch options:', JSON.stringify(fetchOptions, null, 2));
        const response = await fetch(url, fetchOptions);

        console.log('[FroggyAI] SSE start - status:', response.status, 'content-type:', response.headers.get('content-type'));

        // Handle auth errors (a pre-stream retry, FroggyAI only).
        // ⚠️ A 401 and a 403 are DIFFERENT FACTS and carry their status:
        // a 401 is a login that ran out (the caller logs the keeper out);
        // a 403 is a permission refusal (`HasAnyFacilityRoleOrIsAdmin`) —
        // logging somebody out over it throws away a perfectly good token.
        if (response.status === 401 || response.status === 403) {
          const error = new Error(response.status === 401
            ? 'Authentication failed: HTTP 401'
            : 'Permission refused: HTTP 403');
          error.fallback_reason = 'auth_failed';
          error.status = response.status;
          throw error;
        }

        // Handle server errors (a pre-stream retry, FroggyAI only)
        if (response.status >= 500) {
          const error = new Error(`Server error: HTTP ${response.status}`);
          error.fallback_reason = 'server_error';
          error.status = response.status;
          throw error;
        }

        if (!response.ok) {
          // Read error response body for diagnostics
          let errorBody = '';
          try {
            errorBody = await response.text();
          } catch (e) {
            errorBody = 'Unable to read error body';
          }

          console.error(`[FroggyAI] SSE HTTP error - status: ${response.status}, body:`, errorBody);

          const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
          error.fallback_reason = 'http_error';
          error.status = response.status;
          error.responseBody = errorBody;
          // The server's own SENTENCE, when the refusal carried one — never
          // a machine code. See `serverSentenceFrom`.
          error.serverSentence = serverSentenceFrom(errorBody);
          throw error;
        }

        // Check content-type (a pre-stream retry if not SSE, FroggyAI only)
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('text/event-stream')) {
          const error = new Error(`Wrong content-type: ${contentType}`);
          error.fallback_reason = 'content_type_mismatch';
          error.contentType = contentType;
          error.status = response.status;
          throw error;
        }

        streamOpened = true;
        let reader, decoder;
        try {
          reader = response.body.getReader();
          decoder = new TextDecoder();
        } catch (readerError) {
          console.error('[FroggyAI] SSE reader error:', readerError.message, readerError.stack);
          throw new Error(`Failed to get reader: ${readerError.message}`);
        }

        let buffer = '';

        while (!isCompleted && !abortController.signal.aborted) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');

          // Keep last line in buffer if it doesn't end with newline
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmedLine = line.trim();

            if (trimmedLine.startsWith('data: ')) {
              const dataContent = trimmedLine.slice(6);

              // Check for [DONE] marker
              if (dataContent === '[DONE]') {
                isCompleted = true;
                onMessage?.({ type: 'done' });
                onComplete?.({});
                this._closeStream(url);
                return;
              }

              try {
                const data = JSON.parse(dataContent);

                // Log each SSE event type for debugging (one line per type)
                console.log(`[SSE] Event: ${data.type}`, data.type === 'chunk' ? `text_len=${(data.text || '').length}` : '');

                if (data.type === 'start') {
                  onMessage?.({ type: 'start' });
                } else if (data.type === 'delta' || data.type === 'chunk') {
                  // Handle both 'delta' (legacy) and 'chunk' (v2 LangGraph)
                  const text = data.text || '';
                  if (text) {
                    // ⚠️ **THE CHUNK IS FORWARDED WHOLE** (Cowork run 8,
                    // A4). This rebuilt it as `{type, text}` and threw
                    // away every other key the server had put on it —
                    // `narration_superseded` above all, which is the
                    // whole of run 7's C1 fix. So the model's own
                    // unnumbered guess ("Rack 1, Rack 2 or Rack 3")
                    // stayed on screen above the server's NUMBERED
                    // question, two questions for one sentence, and
                    // `NarrationSuperseded.test.js` passed throughout
                    // because it calls `onMessage` itself.
                    //
                    // It is the same bug, in the same function, as the
                    // `asked` flag two branches down — a hand-copied
                    // field list here is a second place for a flag to
                    // go missing. `counts` already forwards whole; so
                    // does this. `type` is normalised to `delta`
                    // (the panel reads delta/text/chunk identically)
                    // and `text` to the value actually sent.
                    onMessage?.({ ...data, type: 'delta', text });
                  }
                } else if (data.type === 'tool_call') {
                  // Pass through tool_call events for UI feedback
                  onMessage?.({ type: 'tool_call', name: data.name, id: data.id });
                } else if (data.type === 'tool_result') {
                  // Pass through tool_result events for UI feedback.
                  // ⚠️ `asked` IS PART OF THE RESULT (S3-06.4 review):
                  // a call that stopped to ask wrote nothing, and this
                  // pass-through dropped the flag on its way to the
                  // panel — so the muted chip only ever appeared in the
                  // tests, which call `onMessage` themselves.
                  // …and so is `question` (Cowork run 38, H2c): a
                  // server question draws no chip at all, and a flag this
                  // copy dropped would put `getEnclosure?` back under it.
                  onMessage?.({
                    type: 'tool_result',
                    name: data.name,
                    success: data.success,
                    asked: !!data.asked,
                    question: !!data.question,
                  });
                } else if (data.type === 'readback') {
                  // I-03 R6 / S3-06.2 — the receipt for a turn that
                  // WROTE ("Fed rack 1 - 7 tanks; skipped 1A"), plus one
                  // undo token per write. Emitted before `done`.
                  onMessage?.({
                    type: 'readback',
                    text: data.text || '',
                    undo_tokens: data.undo_tokens || [],
                  });
                } else if (data.type === 'navigate') {
                  // I-03 R6 / S3-06.3 — the screen moves. ONE per turn
                  // (the backend keeps the LAST successful navigate
                  // tool's target) and always before `done`. The dock
                  // owns what to do with it; this layer only carries it.
                  onMessage?.({ type: 'navigate', to: data.to || null });
                } else if (data.type === 'counts') {
                  // I-03 R6 · S3-06.4 — THE ROUND'S THREE NUMBERS, as
                  // DATA. `readback` emits this for any turn that called
                  // `whatsDue` or `startWalk`; the panel renders it as a
                  // row of its own so the page and the voice cannot
                  // disagree about how much is left today.
                  //
                  // Forwarded WHOLE (`text`, `pending`, `in_progress`,
                  // `overdue`, `open`, `also_overdue`) rather than
                  // field-by-field: this layer carries the chunk, the
                  // panel decides what to draw, and a hand-copied field
                  // list here is a second place for a number to go
                  // missing.
                  onMessage?.({ ...data, type: 'counts' });
                } else if (data.type === 'done') {
                  isCompleted = true;
                  onMessage?.({ type: 'done', usage: data.usage });
                  onComplete?.({ usage: data.usage, tool_call_count: data.tool_call_count, iteration: data.iteration });
                  this._closeStream(url);
                  return;
                } else if (data.type === 'ping') {
                  // A KEEPALIVE (run 13 review, item 2). The server
                  // emits one whenever a RUNNING turn has produced
                  // nothing for 15 seconds, because our chunks only
                  // leave the graph at node boundaries and the model
                  // client retries with sleeps — so silence is not
                  // evidence of a dead socket until eight of these have
                  // been missed. The emitter is
                  // `frogger_ai/langgraph/streaming.py` and ships in
                  // THIS deploy; the panel's watchdog is safe without
                  // it, because a burial leaves the socket open.
                  //
                  // It carries nothing and renders nothing; it is
                  // forwarded so the panel's watchdog can tell a slow
                  // turn from a dead one, and it is listed EXPLICITLY
                  // so it does not ride the unlisted-type WARNING four
                  // times a minute for the life of every long turn.
                  onMessage?.({ type: 'ping' });
                } else if (data.type === 'boundary') {
                  // A NEW MODEL RESPONSE BEGAN (Cowork run 25, closure
                  // item 4). No text; the panel treats it exactly as it
                  // treats a tool call between two narrations — the next
                  // fragment opens a new paragraph. Listed explicitly so
                  // it never rides the unlisted-type WARNING.
                  onMessage?.({ type: 'boundary' });
                } else if (data.type === 'error') {
                  // Pass through error events - call onError instead of throwing.
                  // `server_reported`: the SERVER said this, in its own
                  // words, about its own turn — as opposed to a transport
                  // failure, where nobody on this side knows what landed.
                  const error = new Error(data.message || 'Stream error');
                  error.code = data.code;
                  error.server_reported = true;
                  onError?.(error);
                  this._closeStream(url);
                  return;
                } else {
                  // ⚠️ THIS CHAIN IS A FILTER, NOT A PIPE (S3-06.4, the
                  // Cowork re-sweep). The `counts` chunk was emitted by
                  // the server, logged by the server, handled by
                  // `applyChunk` and rendered by the panel — and never
                  // arrived, because THIS if/else had no arm for it and
                  // an unmatched type falls off the end in silence. The
                  // three numbers were right in the prose and the row
                  // did not exist in the DOM.
                  //
                  // So an unknown type is FORWARDED rather than dropped.
                  // `applyChunk` ignores what it does not know, which is
                  // the same outcome for a genuinely useless chunk and
                  // the opposite one for a chunk somebody added at the
                  // other end and forgot to list here.
                  console.warn('[SSE] forwarding unlisted chunk type:', data.type);
                  onMessage?.({ ...data });
                }
              } catch (parseError) {
                // In strict mode, don't fail on partial chunk parse issues
                if (!strictMode) {
                  console.warn('[SSE] Failed to parse SSE message (non-strict):', dataContent, parseError);
                } else {
                  console.debug('[SSE] Parse error (harmless in strict mode):', parseError.message);
                }
              }
            }
          }
        }

        reader.releaseLock();

      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('[SSE] Stream aborted (intentional)');
          return; // Intentionally aborted
        }

        // A TRANSPORT failure: everything that reaches here happened after
        // the request was handed to fetch, so this side cannot know
        // whether the server received it, ran it, or wrote. The dock reads
        // this flag to say so ("the request may still have gone
        // through") instead of guessing.
        error.transport = true;

        // ⚠️ **A TURN THE SERVER MAY HAVE RECEIVED IS NEVER SENT AGAIN
        // AUTOMATICALLY.** The server keeps running a turn after the
        // client goes (the keepalive worker is JOINED on disconnect), so
        // re-issuing the same GET re-runs the graph: "I fed rack 1"
        // twice is two feedings, an Undo twice is a second reversal, a
        // confirm "yes" twice is a second create. And a retry after
        // chunks have arrived paints the answer — and its read-back and
        // Undo row — a second time. So:
        //   * RETRY_NEVER (the staff dock, the default): no retry at all;
        //   * RETRY_BEFORE_RESPONSE (customer FroggyAI, read-only tools):
        //     a retry only while NO stream has opened — a refused fetch,
        //     a 5xx/401/403, a non-SSE body. Once an SSE response is
        //     open the turn is running and nothing is re-sent.
        // The person decides whether to repeat it, told the truth.
        const mayRetry = retry === RETRY_BEFORE_RESPONSE
          && !streamOpened && !isCompleted;
        if (!mayRetry) {
          console.warn(`[SSE] stream failed, NOT re-sent (retry=${retry}, opened=${streamOpened}): ${error.message}`);
          onError?.(error);
          this._closeStream(url);
          return;
        }

        // Strict mode: a pre-stream retry only for these reasons (a
        // refused fetch is not one of them).
        if (strictMode) {
          const allowedReasons = ['auth_failed', 'server_error', 'content_type_mismatch'];

          if (!allowedReasons.includes(error.fallback_reason)) {
            console.error(`[FroggyAI] Strict mode prevented retry: ${error.message}`);
            const strictError = new Error(`Strict mode prevented retry: ${error.message}`);
            strictError.transport = true;
            onError?.(strictError);
            this._closeStream(url);
            return;
          }
        }

        // Log retry details
        console.warn(`[FroggyAI] SSE retry reason=${error.fallback_reason} status=${error.status || 'unknown'} ctype=${error.contentType || 'unknown'}`)

        if (reconnectAttempts < maxReconnects) {
          reconnectAttempts++;
          console.log(`[SSE] Reconnection attempt ${reconnectAttempts}/${maxReconnects}`);
          // ⚠️ THE RECONNECT STAYS INSIDE THE SAME PROMISE (FroggyAI polish
          // review). This used to schedule `connect()` on a bare setTimeout
          // and RETURN, so the promise this function returns settled while a
          // retry could still deliver the whole answer — and the full-page
          // FroggyAI chat, which finalizes a turn when that promise settles,
          // marked the turn over and then dropped every reconnected chunk.
          // The delay is awaited instead, so the promise still means "the
          // stream is over". A close() during the delay cancels the pending
          // reconnect: the wait ends at once and nothing is fetched.
          await new Promise((resolve) => {
            if (abortController.signal.aborted) { resolve(); return; }
            const timer = setTimeout(resolve, 1000 * reconnectAttempts);
            abortController.signal.addEventListener('abort', () => {
              clearTimeout(timer);
              resolve();
            }, { once: true });
          });
          if (abortController.signal.aborted) {
            console.log('[SSE] Reconnect cancelled (stream closed during the retry delay)');
            return;
          }
          await connect();
        } else {
          console.error('[SSE] Stream failed permanently:', error.message);
          onError?.(error);
          this._closeStream(url);
        }
      }
    };

    await connect();
    return handle;
  }

  // ⚠️ THE JSON-POST FALLBACKS ARE GONE (`_fallbackFroggyAI`,
  // `_fallbackFroggerAI`). Each re-asked the SAME sentence as a POST after
  // the stream had failed — a turn the server may already have run, and
  // for FroggerAI may already have written. A failed turn now ends and
  // says so; the person decides whether to ask again.

  /**
   * Stop a specific stream
   */
  stopStream(url) {
    this._closeStream(url);
  }


  /**
   * Close and cleanup stream
   */
  _closeStream(url) {
    const eventSource = this.activeStreams.get(url);
    if (eventSource) {
      eventSource.close();
      this.activeStreams.delete(url);
    }
  }

  // `askFroggyStream` is gone: nothing in the app called it (the bubble
  // and the full page call `streamFroggyAI` directly), and its only job
  // was to re-ask a failed customer turn through the JSON fallback.

  /**
   * High-level helper for FroggerAI streaming
   * @param {string} facilityId - Facility UUID
   * @param {string} question - User question
   * @param {string} authToken - JWT Bearer token
   * @param {function} onMessage - Message callback
   * @param {function} onError - Error callback
   * @param {function} onComplete - Complete callback
   * @param {string} conversationId - Stable conversation ID for session state persistence
   */
  async askFroggerStream(facilityId, question, authToken, onMessage, onError, onComplete, conversationId = null, place = null, onOpen = null, turnId = null, stoppedTurn = null) {
    const streamStartTime = performance.now();
    let firstByteTime = null;

    const wrappedOnMessage = (data) => {
      if (firstByteTime === null) {
        firstByteTime = performance.now();
        console.info(`[FroggerAI] First byte latency: ${(firstByteTime - streamStartTime).toFixed(2)}ms`);
      }
      onMessage?.(data);
    };

    const wrappedOnComplete = (data) => {
      const totalTime = performance.now() - streamStartTime;
      console.info(`[FroggerAI] Total latency: ${totalTime.toFixed(2)}ms`);
      onComplete?.(data);
    };

    // ⚠️ **A FAILED TURN IS NEVER RE-ASKED** (the hostile review of the
    // FroggyAI polish batch). This used to hand every stream error to a
    // JSON POST of the SAME sentence — after the stream had already
    // retried it twice — so "I fed rack 1" could be run four times, and
    // the server keeps running a turn after the client goes. The error
    // is passed straight to the panel, which ends the turn with the
    // evidential line (`NO_ANSWER_TEXT`) for a transport failure.
    const wrappedOnError = (error) => {
      console.warn('[FroggerAI] Stream error (not re-sent):', error?.message);
      onError?.(error);
    };

    return this.streamFroggerAI(facilityId, question, authToken, wrappedOnMessage, wrappedOnError, wrappedOnComplete, conversationId, place, onOpen, turnId, stoppedTurn);
  }

  /**
   * Stop a stream by URL
   */
  stopStreamByUrl(url) {
    this._closeStream(url);
    const abortController = this.abortControllers.get(url);
    if (abortController) {
      abortController.abort();
      this.abortControllers.delete(url);
    }
  }

  /**
   * Stop all active streams
   */
  stopAllStreams() {
    this.activeStreams.forEach((eventSource, url) => {
      this._closeStream(url);
    });
    this.abortControllers.forEach(controller => controller.abort());
    this.abortControllers.clear();
    this._clearAllInactivityTimers();
  }

  /**
   * Handle tab visibility change for mobile robustness
   */
  handleVisibilityChange() {
    if (document.hidden) {
      // Tab went to background - set inactivity timers for all active streams
      this.activeStreams.forEach((stream, url) => {
        this._setInactivityTimer(url);
      });
    } else {
      // Tab came to foreground - clear timers and resume activity
      this._clearAllInactivityTimers();
      this.activeStreams.forEach((stream, url) => {
        this.lastActivity.set(url, Date.now());
      });
    }
  }

  /**
   * Set inactivity timer for a stream URL
   */
  _setInactivityTimer(url) {
    this._clearInactivityTimer(url);

    const timer = setTimeout(() => {
      console.log(`[Streaming] Inactivity timeout for ${url}`);
      this._pauseStream(url);
    }, this.inactivityTimeout);

    this.inactivityTimers.set(url, timer);
  }

  /**
   * Clear inactivity timer for a stream URL
   */
  _clearInactivityTimer(url) {
    const timer = this.inactivityTimers.get(url);
    if (timer) {
      clearTimeout(timer);
      this.inactivityTimers.delete(url);
    }
  }

  /**
   * Clear all inactivity timers
   */
  _clearAllInactivityTimers() {
    this.inactivityTimers.forEach(timer => clearTimeout(timer));
    this.inactivityTimers.clear();
  }

  /**
   * Pause a stream (mobile-friendly)
   */
  _pauseStream(url) {
    const stream = this.activeStreams.get(url);
    if (stream) {
      console.log(`[Streaming] Pausing stream: ${url}`);
      // Don't close completely, just mark as paused
      // The UI can show a "Tap to resume" message
    }
  }

  /**
   * Check if a stream is paused due to inactivity
   */
  isStreamPaused(url) {
    return this.inactivityTimers.has(url);
  }

  /**
   * Resume a paused stream
   */
  resumeStream(url) {
    this._clearInactivityTimer(url);
    this.lastActivity.set(url, Date.now());
    console.log(`[Streaming] Resumed stream: ${url}`);
  }

  /**
   * Debounced function creator for submit handling
   */
  debounce(func, wait = 300) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Export singleton instance
export const streamingAPI = new StreamingAPIService();